import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IncidentSeverity } from 'api/canvms/incidents';

const SEVERITY_COLORS: Record<IncidentSeverity, string> = {
  critical: 'error.main',
  high: 'warning.main',
  medium: 'warning.light',
  low: 'primary.main',
};

interface DismissConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string) => void;
  incidentTitle?: string;
  severity?: IncidentSeverity;
  loading?: boolean;
  /** When true, show the notes text field */
  showNotes?: boolean;
}

function DismissConfirmDialog({
  open,
  onClose,
  onConfirm,
  incidentTitle,
  severity,
  loading = false,
  showNotes = false,
}: DismissConfirmDialogProps) {
  const [notes, setNotes] = useState('');

  const handleConfirm = () => {
    onConfirm(notes.trim() || undefined);
    setNotes('');
  };

  const handleClose = () => {
    if (loading) return;
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        Dismiss Incident
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Are you sure you want to dismiss this incident? Dismissed incidents cannot be reopened.
        </Typography>

        {incidentTitle && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            {severity && (
              <Chip
                label={severity.toUpperCase()}
                size="small"
                sx={{
                  bgcolor: SEVERITY_COLORS[severity],
                  color: 'common.white',
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                }}
              />
            )}
            <Typography variant="body2" fontWeight="medium">
              {incidentTitle}
            </Typography>
          </Box>
        )}

        {showNotes && (
          <TextField
            label="Dismissal notes (optional)"
            multiline
            rows={3}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reason for dismissal..."
            disabled={loading}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Dismiss Incident
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DismissConfirmDialog;
